import type { CSSProperties } from "react";

/**
 * Governor portrait crop.
 *
 * Every governor detail page (the hero) and the "Historique récent" strip render
 * the portrait inside a fixed `aspect-[4/5]` frame so all governors are the SAME
 * size regardless of the source photo's natural ratio. Two knobs keep each face
 * well framed inside that crop:
 *
 *  - `position` (`object-position`) decides WHICH part of the photo stays visible.
 *    The default `"50% 20%"` self-adapts to the two common shapes: a WIDE headshot
 *    is cropped left/right so `50%` keeps the centred face; a TALL upper-body shot
 *    is cropped top/bottom so `20%` keeps the upper face.
 *  - `zoom` scales the photo INSIDE the (clipped) frame. A few records carry a
 *    full-body standing photo (head + whole body); inside the shared 4/5 frame the
 *    face comes out tiny with lots of body. Zooming into the head+torso makes those
 *    read like the tighter upper-body portraits. Default `1` = no zoom, so the 15
 *    well-framed governors render exactly as before.
 *
 * Overrides are keyed by a stable fragment of the image filename, matched
 * case-insensitively so they survive bucket-path / extension differences across
 * the four environments.
 */
export type GovernorPortraitCrop = {
  /** CSS `object-position` (and the transform-origin when zoomed). */
  position: string;
  /** Scale applied inside the clipped frame (1 = no zoom). */
  zoom: number;
};

const DEFAULT_CROP: GovernorPortraitCrop = { position: "50% 20%", zoom: 1 };

const GOVERNOR_CROP_OVERRIDES: Array<{ match: string; crop: GovernorPortraitCrop }> = [
  // André Wameso — the source (wameso.jpg, 634×928) is a full-body standing photo,
  // so the shared 4/5 crop leaves the face small with a lot of body. Zoom into the
  // head + torso, biased to the top, so it reads like the tighter upper-body
  // portraits (e.g. Mutombo) while staying the same frame size.
  { match: "wameso", crop: { position: "50% 14%", zoom: 1.55 } },
];

function resolveGovernorCrop(src?: string | null): GovernorPortraitCrop {
  const s = (src ?? "").toLowerCase();
  for (const override of GOVERNOR_CROP_OVERRIDES) {
    if (s.includes(override.match)) return override.crop;
  }
  return DEFAULT_CROP;
}

/** Resolve the `object-position` for a governor portrait from its image src. */
export function governorPortraitObjectPosition(src?: string | null): string {
  return resolveGovernorCrop(src).position;
}

/** Resolve the in-frame zoom (transform scale) for a governor portrait. 1 = none. */
export function governorPortraitZoom(src?: string | null): number {
  return resolveGovernorCrop(src).zoom;
}

/**
 * Inline style that zooms the portrait inside its clipped frame. Returns
 * `undefined` when there is no zoom so the markup stays byte-identical for the
 * non-zoomed governors. The transform-origin tracks the focal position so the
 * zoom homes in on the face, not the centre of the frame. Requires the frame
 * wrapper to be `overflow-hidden` (the strip's link and the hero wrapper both are).
 */
export function governorPortraitZoomStyle(src?: string | null): CSSProperties | undefined {
  const { zoom, position } = resolveGovernorCrop(src);
  if (zoom === 1) return undefined;
  return { transform: `scale(${zoom})`, transformOrigin: position };
}
